import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JoyrideService, JoyrideStepComponent } from 'ngx-joyride';
import { SidebarService } from '@app/shared/services/helpers/sidebar.service';
import { BehaviorSubject } from 'rxjs';
import { HmoFormComponent } from '@app/masterfile/hmo/hmo-form/hmo-form.component';
import { ConsentTemplateFormComponent } from '@app/masterfile/consent-templates/consent-template-form/consent-template-form.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SettingsService } from '../api/settings.service';
import { StaffService } from '../api/staff.service';
import { HmoService } from '../mstfapi/hmo.service';
import {
  ADMIN_VIRTUAL_TOUR_FRESH_WINDOW_DAYS,
  NOT_SHOW_ADMIN_VIRTUAL_TOUR,
  SHOW_ADMIN_VIRTUAL_TOUR
} from '@app/shared/constants/settings.constants';
import swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class TourserviceService {
  isTourRunning = true;
  allSteps: any = [
    'dashboard@/manage/dashboard',
    'clinics@/manage/clinics',
    'editclinics@/manage/clinics/',
    'editclinicsdata@/manage/clinics/form/',
    'associates@/manage/associates',
    'staff@/manage/staff',
    'staffAdd@/manage/staff',
    'staffCreate@/manage/staff/form',
    'labs@/manage/laboratories',
    'reports@/manage/reports/collections',
    'masterfile@/manage/masterfile',
    'masterfileHmo@/manage/masterfile/hmo',
    'serviceListMasterfile@/manage/masterfile/services',
    'serviceListMasterfileAdd@/manage/masterfile/services',
    'medicinesMasterfile@/manage/masterfile/medicines',
    'medicalConditionMasterfile@/manage/masterfile/medical-conditions',
    'dentalHabitMasterfile@/manage/masterfile/dental-habits',
    'smsTemplateMasterfile@/manage/masterfile/sms-templates',
    'consentTemplateMasterfile@/manage/masterfile/consent-templates',
    'consentTemplateMasterfileAdd@/manage/masterfile/consent-templates', // modal pop up when empty else proceed to certificateTemplateMasterfile
    'certificateTemplateMasterfile@/manage/masterfile/certificate-templates',
    'certificateTemplateMasterfileAdd@/manage/masterfile/certificate-templates', // modal pop up when empty else proceed to precriptionTemplateMasterfile
    'prescriptionTemplateMasterfile@/manage/masterfile/prescription-templates',
    'prescriptionTemplateMasterfileAdd@/manage/masterfile/prescription-templates', // modal pop up when empty else proceed to referralTemplateMasterfile
    'kioskMasterfile@/manage/masterfile/qr-kiosk-mode',
    'recallApplianceMasterfile@/manage/masterfile/recall-appliance',
    'recallOcclusionMasterfile@/manage/masterfile/recall-occlusion',
    'recallPeriodontalMasterfile@/manage/masterfile/recall-periodontal',
    'recallTmdMasterfile@/manage/masterfile/recall-tmd',
    'toothStatusMasterfile@/manage/masterfile/tooth-status',
    'toothConditionMasterfile@/manage/masterfile/tooth-conditions',
    'toothProsthodonticMasterfile@/manage/masterfile/tooth-prostodontics',
    'toothSurgeryMasterfile@/manage/masterfile/tooth-surgery',
    'toothXrayMasterfile@/manage/masterfile/tooth-xray',
    'settings@/manage/settings/my-account',
    'settingsPrintheader@/manage/settings/header',
    'settingsPrintheaderAdd@/manage/settings/header', // modal pop up when empty else proceed to settingsPrintFooter
    'settingsOnlineBooking@/manage/settings/online-booking',
    'settingsOnlineBookingAdd@/manage/settings/online-booking',
    'tools@/manage/tools/backup',
    'links',
    'adminProfile'
  ];

  /** app-sidebar.js takes the sidebar off-canvas below 1025px. */
  private readonly mobileSidebarMaxWidth = 1024;
  /** Marker read by app-sidebar.js so it stops auto-closing the sidebar mid-tour. */
  private readonly tourActiveBodyClass = 'mmp-tour-active';
  private repositionTimers: any[] = [];
  private viewportListener: (() => void) | null = null;

  private visitedSteps: { [key: string]: boolean } = {};
  private flagStatus = new BehaviorSubject<boolean>(false);
  currentFlagStatus = this.flagStatus.asObservable();

  constructor(
    private joyrideService: JoyrideService,
    private sidebarService: SidebarService,
    private modal: NgbModal,
    private settingsSvc: SettingsService,
    private router: Router,
    private staffService: StaffService,
    private hmoService: HmoService
  ) {
    this.loadVisitedStepsFromLocalStorage();
    this.clearObsoleteDeviceWarningKeys();
  }

  tourServiceStart(step?: any, id?: any) {
    // Onboarding is supported on every device MyMedsPH supports (phone, tablet, iPad,
    // laptop, desktop). There is deliberately no user-agent / touch / viewport gate here:
    // eligibility is decided by the account settings flag alone. Slow-connection handling
    // below is a separate concern and intentionally kept.
    let connection = (navigator as any).connection;
    if (connection) {
      let effectiveType = connection.effectiveType;
      if (effectiveType === '2g' || effectiveType === 'slow-2g') {
        let connectionTourDisabled = localStorage.getItem('connectionTourDisabled');
        let currentTime = new Date().getTime();
        if (!connectionTourDisabled || currentTime - Number(connectionTourDisabled) > 8 * 60 * 60 * 1000) {
          // 8 hours in milliseconds
          swal('Warning!', 'Your internet connection is slow. The tour will not proceed.', 'warning');
          localStorage.setItem('connectionTourDisabled', String(currentTime));
        }
        localStorage.removeItem('visitedSteps');
        this.setFlagStats(false);
        this.sidebarService.setEnableSidebar(true);

        return; // Exit if the connection is 2G or slower
      }
    }

    window.addEventListener('beforeunload', (event) => {
      // Replace this with your actual condition
      if (this.isTourRunning) {
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = '';
      }
    });

    let unvisitedSteps = this.getUnvisitedSteps();
    // Map unvisited steps to include IDs if necessary
    unvisitedSteps = unvisitedSteps.map((step) => {
      if (step === 'editclinicsdata@/manage/clinics/form/' && id) {
        return `${step}${id}`;
      }
      return step;
    });
    if (step) {
      const stepIndex = unvisitedSteps.indexOf(step);
      if (stepIndex !== -1) {
        unvisitedSteps = unvisitedSteps.slice(stepIndex);
      }
    }

    if (!unvisitedSteps.length) {
      return;
    }

    const targetRoute = this.getRouteFromStep(unvisitedSteps[0]);

    this.settingsSvc.getSettings().subscribe(() => {
      if (!this.isAdminVirtualTourActive()) {
        this.sidebarService.setEnableSidebar(true);
        this.joyrideService.closeTour();
        return;
      }

      this.router.navigateByUrl(targetRoute).then(() => {
        this.runJoyride(unvisitedSteps);
      });
    });
  }

  private runJoyride(unvisitedSteps: string[]) {
    this.beginResponsiveTour(unvisitedSteps[0]);
    this.joyrideService
          .startTour({ steps: unvisitedSteps, logsEnabled: false, showPrevButton: false, stepDefaultPosition: 'top' })
          .subscribe(
            (currentStep) => {
              this.ensureStepTargetVisible(currentStep.name);
              const stepNames = [
                'editclinicsdata',
                'staffCreate',
                'masterfileHmo',
                'serviceListMasterfileAdd',
                'consentTemplateMasterfileAdd',
                'certificateTemplateMasterfileAdd',
                'prescriptionTemplateMasterfileAdd',
                'settingsPrintheaderAdd',
                'settingsOnlineBookingAdd'
              ];
              if (stepNames.includes(currentStep.name)) {
                this.sidebarService.setEnableSidebar(false);
                this.joyrideService.closeTour();
                let visitedSteps = this.getVisitedSteps();

                if (currentStep.name === 'staffCreate') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.verifyStaffAndAdvance();
                  }
                }
                if (currentStep.name === 'settingsOnlineBookingAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
                if (currentStep.name === 'settingsPrintheaderAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
                if (currentStep.name === 'masterfileHmo') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.verifyHmoAndAdvance();
                  }
                }
                if (currentStep.name === 'serviceListMasterfileAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
                if (currentStep.name === 'consentTemplateMasterfileAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
                if (currentStep.name === 'certificateTemplateMasterfileAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
                if (currentStep.name === 'prescriptionTemplateMasterfileAdd') {
                  if (visitedSteps[currentStep.name]) {
                    this.tourServiceStart();
                  } else {
                    this.joyrideService.closeTour();
                  }
                }
              }
              if (currentStep.name === 'editclinics') {
                window.addEventListener(
                  'click',
                  (event) => {
                    const nextStep = unvisitedSteps.find((step) => step.startsWith('editclinicsdata@'));
                    if (nextStep) {
                      const id = nextStep.split('/').pop();

                      if (id) {
                        this.sidebarService.setEnableSidebar(false);
                        this.joyrideService.startTour({
                          steps: ['manage/clinics/form/' + nextStep],
                          logsEnabled: false,
                          showPrevButton: false,
                          stepDefaultPosition: 'top'
                        });
                      } else {
                        // event click at button next where class name is joyride-button then close the tour
                        if ((event.target as Element).className === 'joyride-button') {
                          this.joyrideService.closeTour();
                        }
                        // else restart the tour
                        else {
                          this.tourServiceStart();
                        }
                      }
                    } else {
                      this.joyrideService.closeTour();
                    }
                  },
                  { once: true }
                );
              }

              if (currentStep.name === 'adminProfile') {
                this.settingsSvc.save([{ key: 'show_admin_virtual_tour', value: 0 }]).subscribe(() => {
                  this.setFlagStats(false);
                  this.sidebarService.setEnableSidebar(true);
                  this.endResponsiveTour();
                  localStorage.removeItem('visitedSteps');
                  localStorage.removeItem('connectionTourDisabled');
                });
              }
              this.markStepAsVisited(currentStep.name);
            },
            (error) => {
              this.endResponsiveTour();
            },
            () => {
              this.isTourRunning = false;
              this.endResponsiveTour();
            }
          );
  }

  /** Route for the next unvisited step (fresh tours start at dashboard). */
  getResumeRoute(): string {
    this.loadVisitedStepsFromLocalStorage();
    const unvisitedSteps = this.getUnvisitedSteps();
    if (!unvisitedSteps.length) {
      return '/manage/dashboard';
    }
    return this.getRouteFromStep(unvisitedSteps[0]);
  }

  isFreshTourStart(): boolean {
    this.loadVisitedStepsFromLocalStorage();
    return Object.keys(this.visitedSteps).length === 0;
  }

  /** Persist ON flag when tour is active but DB row/value is still unset (register gap / legacy). */
  ensureAdminVirtualTourFlagOn(): void {
    const raw = this.normalizeTourFlag(this.settingsSvc.get('show_admin_virtual_tour'));
    if (raw === NOT_SHOW_ADMIN_VIRTUAL_TOUR.toString() || raw === SHOW_ADMIN_VIRTUAL_TOUR.toString()) {
      return;
    }
    this.settingsSvc.patchSettingValue('show_admin_virtual_tour', SHOW_ADMIN_VIRTUAL_TOUR.toString());
    this.settingsSvc.save([{ key: 'show_admin_virtual_tour', value: SHOW_ADMIN_VIRTUAL_TOUR }]).subscribe();
  }

  private getRouteFromStep(step: string): string {
    const separatorIndex = step.indexOf('@');
    if (separatorIndex === -1) {
      return '/manage/dashboard';
    }
    const route = step.slice(separatorIndex + 1);
    return route.startsWith('/') ? route : `/${route}`;
  }

  joyrideServiceCloseTour() {
    this.endResponsiveTour();
    this.joyrideService.closeTour();
  }

  /**
   * Lets the user explicitly skip an optional onboarding step (e.g. HMO creation)
   * without submitting real data, then resumes the tour from the given next step.
   * The step is still shown/paused as normal first; this only removes the
   * requirement to create a record before the tour can continue.
   */
  skipStep(step: string, nextStep: string) {
    if (!this.isAdminVirtualTourActive()) {
      return;
    }
    this.visitedSteps[step] = true;
    localStorage.setItem('visitedSteps', JSON.stringify(this.visitedSteps));
    this.settingsSvc.save([{ key: 'visitedSteps', value: JSON.stringify(this.visitedSteps) }]).subscribe();
    this.joyrideService.closeTour();
    this.tourServiceStart(nextStep);
  }

  /**
   * Self-healing fallback for the staff-creation onboarding step.
   *
   * `visitedSteps['staffCreate']` can still be false here even though a staff
   * record already exists. The staff list page has its own pre-check that marks
   * this step visited and skips ahead, but that check is asynchronous (it waits
   * on a settings fetch + a staff fetch), while tourServiceStart() locks in the
   * joyride step list synchronously, before navigating. On a slow connection or
   * device the admin can click through to this step before that pre-check
   * resolves, so the step list still contains 'staffCreate' and this gate is
   * all that stands between them and getting stuck.
   *
   * Instead of trusting the stale visitedSteps snapshot and closing the tour
   * with no way forward, do one authoritative live check here before giving up.
   * This is what makes the fix race-proof instead of just "usually fast enough".
   */
  private verifyStaffAndAdvance() {
    this.staffService.getInitialRecord({ limit: 1, page: 1, recordType: 'paginated' }).subscribe(
      (data: any) => {
        if (data && data.data && data.data.length > 0) {
          this.markStepAsUnvisitedRequiredInputData('staffCreate', data.data[0]);
        } else {
          this.joyrideService.closeTour();
        }
      },
      () => {
        this.joyrideService.closeTour();
      }
    );
  }

  /**
   * Same self-healing fallback as verifyStaffAndAdvance(), for the HMO step.
   * The "Skip this step" button (see skipStep()) covers the intentional case
   * where the admin has no HMO and doesn't want to add one. This covers the
   * race where an HMO already exists but the masterfile page's own pre-check
   * hadn't resolved yet when the tour reached this gate.
   */
  private verifyHmoAndAdvance() {
    this.hmoService.getAll().subscribe(
      (hmo: any) => {
        if (hmo && hmo.length > 0) {
          this.markStepAsUnvisitedRequiredInputData('masterfileHmoAdd', hmo[0]);
        } else {
          this.joyrideService.closeTour();
        }
      },
      () => {
        this.joyrideService.closeTour();
      }
    );
  }

  setFlagStats(flagStatus: boolean) {
    this.flagStatus.next(flagStatus);
  }

  getFlagStatus() {
    return this.flagStatus;
  }

  private getUnvisitedSteps() {
    return this.allSteps.filter((step) => {
      const stepKey = step.split('@')[0];
      return !Object.keys(this.visitedSteps).some((visitedStep) => visitedStep.startsWith(stepKey));
    });
  }

  private markStepAsVisited(step: string) {
    const stepsToCheck = [
      'clinics',
      'editclinicsdata',
      'editclinics',
      'staff',
      'staffAdd',
      'staffCreate',
      'masterfile',
      'masterfileHmo',
      'masterfileHmoAdd',
      'serviceListMasterfile',
      'serviceListMasterfileAdd',
      'consentTemplateMasterfile',
      'certificateTemplateMasterfile',
      'prescriptionTemplateMasterfile',
      'settings',
      'settingsPrintheader',
      'settingsPrintheaderAdd',
      'settingsOnlineBooking',
      'settingsOnlineBookingAdd'
    ];
    if (stepsToCheck.some((s) => step.includes(s))) {
      // do nothing for now
    } else {
      this.visitedSteps[step] = true;
      localStorage.setItem('visitedSteps', JSON.stringify(this.visitedSteps));
      this.settingsSvc.save([{ key: 'visitedSteps', value: JSON.stringify(this.visitedSteps) }]).subscribe();
    }
  }
  markStepAsUnvisitedRequiredInputData(step: string, data?: any, restartTour = true) {
    if (!this.isAdminVirtualTourActive()) {
      return;
    }
    if (data) {
      if (step.includes('staffCreate')) {
        this.visitedSteps['staff'] = true;
        this.visitedSteps['staffAdd'] = true;
        this.visitedSteps['staffCreate'] = true;
      }
      if (step.includes('editclinicsdata')) {
        this.visitedSteps['clinics'] = true;
        this.visitedSteps['editclinics'] = true;
        this.visitedSteps['editclinicsdata'] = true;
      }
      if (step.includes('masterfileHmoAdd')) {
        this.visitedSteps['masterfile'] = true;
        this.visitedSteps['masterfileHmo'] = true;
        this.visitedSteps['masterfileHmoAdd'] = true;
      }
      if (step.includes('serviceListMasterfileAdd')) {
        this.visitedSteps['serviceListMasterfile'] = true;
        this.visitedSteps['serviceListMasterfileAdd'] = true;
      }
      if (step.includes('consentTemplateMasterfileAdd')) {
        this.visitedSteps['consentTemplateMasterfile'] = true;
        this.visitedSteps['consentTemplateMasterfileAdd'] = true;
      }
      if (step.includes('certificateTemplateMasterfileAdd')) {
        this.visitedSteps['certificateTemplateMasterfile'] = true;
        this.visitedSteps['certificateTemplateMasterfileAdd'] = true;
      }
      if (step.includes('prescriptionTemplateMasterfileAdd')) {
        this.visitedSteps['prescriptionTemplateMasterfile'] = true;
        this.visitedSteps['prescriptionTemplateMasterfileAdd'] = true;
      }
      if (step.includes('settingsPrintheaderAdd')) {
        this.visitedSteps['settings'] = true;
        this.visitedSteps['settingsPrintheader'] = true;
        this.visitedSteps['settingsPrintheaderAdd'] = true;
      }
      if (step.includes('settingsOnlineBookingAdd')) {
        this.visitedSteps['settingsOnlineBooking'] = true;
        this.visitedSteps['settingsOnlineBookingAdd'] = true;
      }
    }
    localStorage.setItem('visitedSteps', JSON.stringify(this.visitedSteps));
    this.settingsSvc.save([{ key: 'visitedSteps', value: JSON.stringify(this.visitedSteps) }]).subscribe();
    if (restartTour) {
      this.tourServiceStart();
    }
  }
  loadVisitedStepsFromLocalStorage() {
    const visitedStepsJson = localStorage.getItem('visitedSteps');
    if (visitedStepsJson) {
      this.visitedSteps = JSON.parse(visitedStepsJson);
    }
  }
  // create a get function to get the value of the localstorage visitedSteps
  getVisitedSteps() {
    return this.visitedSteps;
  }


  /**
   * Legacy "your device is not supported" throttle keys. The laptop/desktop-only warning is
   * gone, and `admin-tour.component.ts` used to read `deviceTourDisabled` while writing
   * `deviceTour`, so the throttle never engaged and the warning re-fired on every open.
   * Both keys are dead now; clear them once so existing installs stop carrying the state.
   */
  private clearObsoleteDeviceWarningKeys(): void {
    try {
      localStorage.removeItem('deviceTourDisabled');
      localStorage.removeItem('deviceTour');
    } catch (e) {
      // storage can be unavailable (private mode); the keys are inert either way
    }
  }

  /**
   * Prepares responsive/off-canvas handling for a tour segment.
   *
   * Deliberately does NOT touch `isTourRunning`: that flag arms the beforeunload
   * "leave site?" prompt, and onboarding restarts a segment after every modal step, so
   * re-arming it here would prompt on every mid-tour refresh - the flow AC 8 requires to
   * resume cleanly.
   */
  private beginResponsiveTour(firstStep?: string): void {
    this.setTourActiveClass(true);
    this.registerViewportListeners();
    this.ensureStepTargetVisible(this.getStepName(firstStep));
  }

  /** Tears down everything beginResponsiveTour() put in place. */
  private endResponsiveTour(): void {
    this.setTourActiveClass(false);
    this.unregisterViewportListeners();
  }

  private setTourActiveClass(active: boolean): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }
    if (active) {
      document.body.classList.add(this.tourActiveBodyClass);
    } else {
      document.body.classList.remove(this.tourActiveBodyClass);
    }
  }

  private getStepName(step?: string): string {
    if (!step) {
      return '';
    }
    return step.split('@')[0];
  }

  /**
   * Below 1025px app-sidebar.js parks `.app-sidebar` off-canvas (`hide-sidebar` =
   * `translate3d(calc(-100% - 1rem), 0, 0)`). The anchors for the sidebar steps stay in the
   * DOM with an off-screen bounding rect, so joyride would happily draw the tooltip outside
   * the viewport. Reveal the sidebar first, then ask joyride to redraw against the settled
   * geometry.
   */
  private ensureStepTargetVisible(stepName?: string): void {
    if (!stepName || typeof document === 'undefined') {
      return;
    }
    if (window.innerWidth > this.mobileSidebarMaxWidth) {
      return;
    }
    const anchor = this.findStepAnchor(stepName);
    if (!anchor || !anchor.closest) {
      return;
    }
    const sidebar = anchor.closest('.app-sidebar') as HTMLElement | null;
    if (!sidebar || !sidebar.classList.contains('hide-sidebar')) {
      return;
    }
    sidebar.classList.remove('hide-sidebar');
    this.requestStepReposition();
  }

  private findStepAnchor(stepName: string): HTMLElement | null {
    const safeName = stepName.replace(/["\\]/g, '\\$&');
    return document.querySelector(
      '[joyridestep="' + safeName + '"], [data-tour-step="' + safeName + '"]'
    ) as HTMLElement | null;
  }

  /**
   * ngx-joyride redraws the step and backdrop on `window resize`, but iOS Safari reports
   * stale geometry on the first resize after a rotation, and the sidebar reveal above has a
   * 0.4s transform transition. Nudge joyride again once the layout has settled.
   */
  private requestStepReposition(): void {
    this.clearRepositionTimers();
    this.dispatchResize();
    this.repositionTimers.push(setTimeout(() => this.dispatchResize(), 150));
    this.repositionTimers.push(setTimeout(() => this.dispatchResize(), 450));
  }

  private dispatchResize(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {
      const legacyEvent = document.createEvent('Event');
      legacyEvent.initEvent('resize', true, true);
      window.dispatchEvent(legacyEvent);
    }
  }

  private clearRepositionTimers(): void {
    this.repositionTimers.forEach((timer) => clearTimeout(timer));
    this.repositionTimers = [];
  }

  private registerViewportListeners(): void {
    if (this.viewportListener || typeof window === 'undefined') {
      return;
    }
    this.viewportListener = () => this.requestStepReposition();
    // Only orientationchange: plain window resizes are already handled by ngx-joyride's own
    // listener, and a visualViewport listener would also fire on every iOS scroll/keyboard
    // event, fanning each one out into synthetic resizes for no acceptance-criteria gain.
    window.addEventListener('orientationchange', this.viewportListener);
  }

  private unregisterViewportListeners(): void {
    this.clearRepositionTimers();
    if (!this.viewportListener || typeof window === 'undefined') {
      this.viewportListener = null;
      return;
    }
    window.removeEventListener('orientationchange', this.viewportListener);
    this.viewportListener = null;
  }

  /**
   * `1` or missing = onboarding active. Stale flag and/or abandoned progress (setting rows older than
   * fresh window) is auto-saved as `0` so established accounts do not re-enter onboarding.
   */
  isAdminVirtualTourActive(): boolean {
    const raw = this.normalizeTourFlag(this.settingsSvc.get('show_admin_virtual_tour'));
    if (raw === NOT_SHOW_ADMIN_VIRTUAL_TOUR.toString()) {
      return false;
    }
    if (this.hasActiveTourProgress()) {
      if (this.isStaleTourProgress()) {
        this.markAdminVirtualTourCompleted(false);
        return false;
      }
      return true;
    }
    if (raw === null) {
      const row = this.settingsSvc.getSettingRow('show_admin_virtual_tour');
      if (!row) {
        return true;
      }
      if (this.isStaleTourSetting()) {
        this.markAdminVirtualTourCompleted(false);
        return false;
      }
      return true;
    }
    if (raw === SHOW_ADMIN_VIRTUAL_TOUR.toString()) {
      if (this.isStaleTourSetting()) {
        this.markAdminVirtualTourCompleted(false);
        return false;
      }
      return true;
    }
    return false;
  }

  private normalizeTourFlag(raw: any): string | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    return String(raw);
  }

  markAdminVirtualTourCompleted(clearVisitedSteps = true): void {
    const payload: { key: string; value: number | string }[] = [
      { key: 'show_admin_virtual_tour', value: NOT_SHOW_ADMIN_VIRTUAL_TOUR }
    ];
    this.settingsSvc.patchSettingValue('show_admin_virtual_tour', NOT_SHOW_ADMIN_VIRTUAL_TOUR.toString());
    if (clearVisitedSteps) {
      this.visitedSteps = {};
      localStorage.removeItem('visitedSteps');
      this.settingsSvc.patchSettingValue('visitedSteps', '{}');
      payload.push({ key: 'visitedSteps', value: JSON.stringify({}) });
    }
    this.settingsSvc.save(payload).subscribe();
    this.setFlagStats(false);
  }

  private hasActiveTourProgress(): boolean {
    const fromSettings = this.parseVisitedStepsJson(this.settingsSvc.get('visitedSteps'));
    if (Object.keys(fromSettings).length > 0) {
      return true;
    }
    this.loadVisitedStepsFromLocalStorage();
    return Object.keys(this.visitedSteps).length > 0;
  }

  private parseVisitedStepsJson(raw: any): Record<string, boolean> {
    if (!raw) {
      return {};
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private isStaleTourProgress(): boolean {
    const fromSettings = this.parseVisitedStepsJson(this.settingsSvc.get('visitedSteps'));
    const hasSettingsProgress = Object.keys(fromSettings).length > 0;
    this.loadVisitedStepsFromLocalStorage();
    const hasLocalProgress = Object.keys(this.visitedSteps).length > 0;
    if (!hasSettingsProgress && !hasLocalProgress) {
      return false;
    }
    const progressRow = this.settingsSvc.getSettingRow('visitedSteps');
    if (progressRow && hasSettingsProgress) {
      return this.isStaleSettingRow(progressRow);
    }
    return this.isStaleTourSetting();
  }

  private isStaleTourSetting(): boolean {
    return this.isStaleSettingRow(this.settingsSvc.getSettingRow('show_admin_virtual_tour'));
  }

  private isStaleSettingRow(row: { updatedDt?: string } | undefined): boolean {
    if (!row) {
      return false;
    }
    const updatedMs = row.updatedDt ? Date.parse(row.updatedDt) : 0;
    const freshWindowMs = ADMIN_VIRTUAL_TOUR_FRESH_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    if (updatedMs > 0 && Date.now() - updatedMs <= freshWindowMs) {
      return false;
    }
    return true;
  }
}
